import { Component, Input, OnChanges, SimpleChanges, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import { ClientSideRowModelModule, GridOptions, themeQuartz, type ColDef, RowAutoHeightModule, ModuleRegistry, CellSpanModule, RowStyleModule } from 'ag-grid-community';
import { GroupingTypeKey, groupingTypes, MarketingResearchCategory, MESSAGING_BRAND_HEADER_WIDTH, THEMES } from '../types';
import { formatFullDate } from '../../../utils/time';
import { PerformanceTrendRendererComponent } from '../../../shared/components/performance-trend-renderer/performance-trend-renderer.component';
import { ScoreBarRendererComponent } from '../../../shared/components/score-bar-renderer/score-bar-renderer.component';
import { CreativesRendererComponent } from '../../../shared/components/creatives-renderer/creatives-renderer.component';
import { ShortNumberFormatPipe } from '../../../pipes/short-number-format.pipe';
import { PossessivePipe } from '../../../pipes/possessive.pipe';
import { DashboardService } from '../../../services/dashboard.service';
import { NoDataRendererComponent } from '../../../shared/components/no-data-renderer/no-data-renderer.component';
import { AssetVersionPipe } from '../../../pipes/asset-version.pipe';
import { MetricCellRendererComponent } from './metric-cell-renderer/metric-cell-renderer.component';
import { tooltips } from '../../../utils/tooltips';
import { MessagingMetric } from '../types';

interface MetricRow {
  metric: string;
  [brandKey: string]: number | string | boolean | object | undefined;
}

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  RowAutoHeightModule,
  CellSpanModule,
  RowStyleModule
]);

@Component({
  selector: 'app-messaging-category-table',
  imports: [AgGridAngular, CommonModule, AssetVersionPipe],
  templateUrl: './messaging-category-table.component.html',
  styleUrl: './messaging-category-table.component.scss'
})
export class MessagingCategoryTableComponent implements OnChanges {
  @Input() data!: MarketingResearchCategory;
  @Input() maxBrandColumns: number = 6;
  @Input() groupingType: GroupingTypeKey = THEMES;
  
  private shortNumberFormatPipe = new ShortNumberFormatPipe();
  private possessivePipe = new PossessivePipe();
  private dashboardService = inject(DashboardService);

  private readonly METRIC_TOOLTIPS: Record<string, string> = {
    [MessagingMetric.IMPRESSION_SHARE]: tooltips.messaging.impressionShare,
  };
  brandKeys = computed(() => {
    const myBrand = this.dashboardService.myBrand();
    const competitors = this.dashboardService.myCompetitors();
    if (myBrand?.key) {
      return [myBrand, ...competitors]
        .filter(b => b?.key)
        .slice(0, this.maxBrandColumns)
        .map(b => b.key);
    }
    return [];
  });

  brandItems = computed(() => {
    const myBrand = this.dashboardService.myBrand();
    const competitors = this.dashboardService.myCompetitors();
    if (myBrand?.key) {
      return [myBrand, ...competitors]
        .filter(b => b?.key)
        .slice(0, this.maxBrandColumns);
    }
    return [];
  });

  rowData: MetricRow[] = [];
  filteredRowData: MetricRow[] = [];
  colDefs: ColDef[] = [];
  isExpanded: boolean = false;
  expandText: string = 'Expand';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data) {
      this.rowData = this.initRowData();
      this.filteredRowData = this.getFilteredRowData();
      this.colDefs = this.initColDefs();
    }
  }

  private getFilteredRowData(): MetricRow[] {
    if (this.isExpanded) {
      return this.rowData;
    }
    return this.rowData.filter(row =>
      row.metric === MessagingMetric.CREATIVES || row.metric === MessagingMetric.IMPRESSION_SHARE
    );
  }

  toggleExpand(): void {
    this.isExpanded = !this.isExpanded;
    this.expandText = this.isExpanded ? 'Collapse' : 'Expand';
    this.filteredRowData = this.getFilteredRowData();
  }

  private initRowData(): MetricRow[] {
    const brands = this.data?.brands || [];
    if (brands.length === 0) return [];

    const brandKeys = this.brandKeys();
    const brandsByKey = new Map(brands.map(b => [b.brandKey, b]));

    const sharedParams = {
      containerStyle: { gap: '24px', width: '153px', justifyContent: 'flex-end' },
    }

    const metricDataAccessors: Record<string, (brand: any, brandKey: string) => any> = {
      [MessagingMetric.CREATIVES]: (brand) => ({
        creatives: brand.current.creatives?.map((c: any) => c.imgUrl).filter((url: string) => url) || [],
        creativeCount: brand.current.creativeCount || 0,
        creativeObjects: brand.current.creatives || []
      }),
      [MessagingMetric.IMPRESSION_SHARE]: (brand, brandKey) => ({
        value: brand.current.impressionShare,
        class: this.shouldShowRedScore(brandKey) ? 'red' : ''
      }),
      [MessagingMetric.SPEND]: (brand) => ({ value: brand.current.spend ? this.shortNumberFormatPipe.transform(brand.current.spend.toFixed(2), 100, '$', '', '<$100') : 'N/A', trend: brand.diff?.spend ?? 0, ...sharedParams }),
      [MessagingMetric.IMPRESSIONS]: (brand) => ({ value: this.shortNumberFormatPipe.transform(brand.current.impressions, 100, '', ''), trend: brand.diff?.impressions ?? 0, ...sharedParams }),
      [MessagingMetric.ATTENTION]: (brand) => ({ value: `${brand.current.attention.toFixed(0)}%`, trend: brand.diff?.attention ?? 0, ...sharedParams }),
      [MessagingMetric.CTR]: (brand) => ({ value: `${this.formatNumber(brand.current.ctr)}%`, trend: brand.diff?.ctr ?? 0, ...sharedParams }),
      [MessagingMetric.LAST_SEEN]: (brand) => brand.current.lastSeen,
    };

    const metricOrder = [
      MessagingMetric.CREATIVES,
      MessagingMetric.IMPRESSION_SHARE,
      MessagingMetric.SPEND,
      MessagingMetric.IMPRESSIONS,
      MessagingMetric.ATTENTION,
      MessagingMetric.CTR,
      MessagingMetric.LAST_SEEN,
    ];
    const rows: MetricRow[] = metricOrder.map(metric => ({ metric }));

    for (let i = 0; i < this.maxBrandColumns; i++) {
      const brandKey = brandKeys[i];
      const fieldKey = brandKey ? `brand_${brandKey}` : `brand_${i}`;
      const brand = brandKey ? brandsByKey.get(brandKey) : undefined;

      rows.forEach(row => {
        if (brand) {
          const accessor = metricDataAccessors[row.metric];
          if (accessor) {
            row[fieldKey] = accessor(brand, brandKey);
          }
        } else {
          row[fieldKey] = undefined;
        }
      });
    }

    return rows;
  }
  get categoryDisplayName(): string {
    return this.groupingType === THEMES ? 
            this.data.name : 
            this.data.name + ` ${groupingTypes[this.groupingType]}`;
  }
  private initColDefs(): ColDef[] {
    const brands = this.data?.brands || [];
    const brandKeys = this.brandKeys();
    const brandsByKey = new Map(brands.map(b => [b.brandKey, b]));
    const metricFormatters: Record<string, (value: any) => string> = {
      [MessagingMetric.LAST_SEEN]: (value) => value == null || value === '' ? '' : formatFullDate(value),
    };

    const metricCellRendererSelectors: Record<string, any> = {
      [MessagingMetric.CREATIVES]: CreativesRendererComponent,
      [MessagingMetric.IMPRESSION_SHARE]: ScoreBarRendererComponent,
      [MessagingMetric.SPEND]: PerformanceTrendRendererComponent,
      [MessagingMetric.IMPRESSIONS]: PerformanceTrendRendererComponent,
      [MessagingMetric.ATTENTION]: PerformanceTrendRendererComponent,
      [MessagingMetric.CTR]: PerformanceTrendRendererComponent,
    };

    const brandColDefs: ColDef[] = [];

    for (let i = 0; i < this.maxBrandColumns; i++) {
      const brandKey = brandKeys[i];
      const fieldKey = brandKey ? `brand_${brandKey}` : `brand_${i}`;
      const brand = brandKey ? brandsByKey.get(brandKey) : undefined;

      const colDef: ColDef = {
        field: fieldKey,
        headerName: '',
        flex: 1,
        valueGetter: (params: any) => {
          return params.data?.[fieldKey];
        },
      };

      if (brand) {
        colDef.cellRendererSelector = (params: any) => {
          const metric = params.data?.metric;
          const component = metricCellRendererSelectors[metric];

          if (component) {
            const result: any = { component };
            const value = params.value as any;
            if (component === ScoreBarRendererComponent) {
              result.params = {
                class: value?.class || ''
              };
            }

            if (component === CreativesRendererComponent) {
              result.params ??= {};
              result.params.value ??= {}
              Object.assign(result.params.value, {
                brandKey, 
                theme: this.data.name, 
                groupingType: this.groupingType
              })
            }

            return result;
          }

          return undefined;
        };

        colDef.valueFormatter = (params: any) => {
          const metric = params.data?.metric;
          const component = metricCellRendererSelectors[metric];

          if (component) {
            return params.value ?? '';
          }

          const formatter = metricFormatters[metric];
          return formatter ? formatter(params.value) : (params.value ?? '');
        };
      }

      else {
        colDef.cellRendererSelector = (params) => {
          const possessiveBrandName = this.possessivePipe.transform(this.brandItems()[i]?.label || '');
          return {
            component: NoDataRendererComponent,
            params: {
              value: this.groupingType === THEMES ? 
              `The theme is not present in ${possessiveBrandName} creatives` : 
              `${this.brandItems()[i]?.label} has no creatives in this performance segment`,
              class: possessiveBrandName === '' ? 'grey' : i === 0 ? 'red' : ''
            }
          };
        };
        colDef.spanRows = true;
      }

      if (i === 0) {
        colDef.colId = `my-brand-column`;
      }

      if (!brandKey) {
        colDef.colId = `no-brand-column${i}`
      }

      brandColDefs.push(colDef);
    }

    return [
      {
        field: 'metric',
        headerName: '',
        pinned: 'left',
        flex: 0,
        width: MESSAGING_BRAND_HEADER_WIDTH,
        colId: 'metric-column',
        cellRenderer: MetricCellRendererComponent,
        cellRendererParams: (params: any) => ({ tooltip: this.METRIC_TOOLTIPS[params.value] ?? '' }),
      },
      ...brandColDefs,
    ];
  }

  theme = themeQuartz.withParams({
    spacing: 10,
    rowHoverColor: 'transparent',
    fontSize: '14px',
    fontFamily: 'Poppins',
    cellTextColor: '#717172',
    cellHorizontalPadding: 10,
  });
  modules = [ClientSideRowModelModule, RowAutoHeightModule, CellSpanModule];
  gridOptions: GridOptions | any = {
    suppressMovableColumns: true,
    domLayout: 'autoHeight',
    rowHeight: undefined,
    headerHeight: 0,
    enableCellSpan: true,
    getRowClass:(params:any) =>{
      const metric = params.data?.metric;
      if ([MessagingMetric.CREATIVES, MessagingMetric.IMPRESSIONS, MessagingMetric.CTR].includes(metric)){
        return 'bottom-border-highlighted';
      }
      return '';
    },
    getRowHeight: (params: any) => {
      if (params.data?.metric === MessagingMetric.CREATIVES) {
        return 126;
      }
      return undefined;
    },
    defaultColDef: {
      resizable: false,
      autoHeight: true,
      sortable: false,
      wrapText: true,
      flex: 1
    },
  };

  private formatNumber(value: number | string): string {
    if (typeof value === 'string') return value;
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  private shouldShowRedScore(brandKey: string): boolean {
    const brandKeys = this.brandKeys();
    if (brandKeys.length === 0 || brandKey !== brandKeys[0]) {
      return false;
    }
    const brands = this.data?.brands || [];
    if (brands.length === 0) return false;
    const brandsByKey = new Map(brands.map(b => [b.brandKey, b]));
    const myBrand = brandsByKey.get(brandKey);
    if (!myBrand) return false;

    const competitorsWithImpressions = brandKeys.slice(1)
      .map(key => brandsByKey.get(key))
      .filter(brand => brand && brand.current.impressions > 0)
      .length;

    const meImpressionShare = myBrand.current.impressionShare;
    const thresholds: Record<number, number> = {
      5: 12,
      4: 15,
      3: 20,
      2: 25,
      1: 40,
    };
    const threshold = thresholds[competitorsWithImpressions];
    return threshold !== undefined && meImpressionShare < threshold;
  }
}

